import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  EkipIkon,
  KameraIkon,
  KapatIkon,
  KilitIkon,
  NoktaIkon,
  NotIkon,
  OkAsagiIkon,
  OnayIkon,
} from '../lib/ikon';
import { api, sahaKomutu } from '../lib/api';
import { useSenkron } from '../lib/senkron';
import { KesifForm } from './KesifForm';
import { FotoEkle } from './FotoEkle';
import { AdimDuzelt } from './AdimDuzelt';
import { GeriDugme } from '../lib/dugme';
import { FazAksiyon } from './FazAksiyon';
import { FotoSerit } from '../lib/foto-goster';
import {
  NihaiMetrajSecici,
  OlcuListesi,
  metrajGoster,
  nihaiOlculer,
  nihaiToplam,
  type ManuelOlcu,
} from '../lib/olcu';
import type { DuzeltmeAlani, IskeleTipi, PhotoStep } from '@mod/domain';
import { duzeltilebilirMi, fotoGecersizIsaretlenebilirMi, k5_izinFotosuVarMi } from '@mod/domain';
import { useOturum } from '../lib/oturum';

/**
 * SAHA İŞ DETAYI — akordeon adımlar (devir dokumani Bolum 6.11).
 * Prototip referansi: `mSteps()` + `mAcc()`.
 *
 * ADIM DURUMU DORT: done / current / locked (+ gate isareti).
 * SIRADAKI ADIM OTOMATIK ACIK gelir — §1.4 "minimum tik".
 */

type Durum = 'done' | 'current' | 'locked';

/**
 * GERİ ALINABİLİR TİKLER — sunucudaki `GERI_ALINABILIR` ile aynı küme.
 * Teslim ve söküm kapanışı YOK: kanıt zinciri mühürlenmiş, müşteriye mail
 * gitmiştir. Onun geri dönüşü "güvenli değil" akışıdır, sessiz silme değil.
 */
const GERI_ALINABILIR_YOL = ['izin', 'malzeme', 'kurulum-basla', 'kurulum-bitti'];
/** Bandın ekranda kalma süresi. Sunucu penceresi (10 dk) daha geniş — bant
 *  kaybolsa bile şef yine geri alabilir. */
const GERI_AL_GORUNME_MS = 90_000;

interface Detay {
  id: string;
  isEmriNo: string | null;
  tanim: string;
  unite: string;
  /** Ünite düzeltmesi kimlikle gider — ad tekrar edebilir. */
  uniteId: string;
  durum: string;
  isEmriFotoVar: boolean;
  /** Teslim damgası — BİR KEZ olur, geri alınamaz. Adım durumunun ölçütü. */
  teslimAt: string | null;
  /** İKİ AYRI TOPLAM — karışık iş tek sayıya indirgenmez (02.08.2026). */
  metrajM3: number;
  metrajM2: number;
  /** m³ + m² × 4 — tek sayı gerektiğinde. */
  esdegerM3: number;
  kesifNot: string | null;
  izin: 'alindi' | 'override' | null;
  izinNotu: string | null;
  malzemeSevk: boolean;
  olculer: { ad: string | null; tip?: IskeleTipi | null; en: number | null; boy: number | null; yuk: number | null; yaricap?: number | null }[];
  iskeleOnayNo: string | null;
  /** SAP iş emri alanları — yalnız SOCAR gibi müşterilerde dolu. */
  sorumluIsyeri?: string | null;
  planlamaGrubu?: string | null;
  /** SAP önceliği (1-6) — K6: bilgi, sıralamaya girmez. */
  oncelik?: number | null;
  /** Dış proje bölgesi: K5 izin kâğıdı istenmez. */
  izinGerekmez?: boolean;
  fazlar: {
    tip: string;
    durum: string | null;
    revizeTur: string | null;
    not: string | null;
    ekip: string[];
    ekipIds?: string[];
    ekipBasiId?: string | null;
    basladiAt: string | null;
    bittiAt: string | null;
  }[];
  fotolar: {
    fotoNo: number;
    step: string;
    contentType?: string | null;
    /** Yanlış yüklenen kâğıt — kayıt durur, sayılmaz (K8). */
    gecersiz?: boolean;
    gecersizNeden?: string | null;
  }[];
}

export function SahaIsDetay({
  id,
  onGeri,
  /** Geri düğmesinin metni — geldiği ekranın adı (bkz. lib/geri.ts). */
  geriEtiket = 'İşlerim',
}: {
  id: string;
  onGeri: () => void;
  geriEtiket?: string;
}) {
  const [d, setD] = useState<Detay | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [elleAcilan, setElleAcilan] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [duzeltAdim, setDuzeltAdim] = useState<DuzeltmeAlani | null>(null);
  const [overrideAcik, setOverrideAcik] = useState(false);
  const [overrideNot, setOverrideNot] = useState('');
  /** Kontrol & teslim panelindeki iskele onay no — şef mobilde de teslim eder. */
  const [onayNo, setOnayNo] = useState('');
  /** Nihai metraj elle mi girildi — `null` ise keşif ölçüsü aynen (varsayılan). */
  const [manuel, setManuel] = useState<ManuelOlcu[] | null>(null);
  /** Son geri alınabilir tikin zamanı — "Geri Al" bandı bu pencerede görünür. */
  const [geriAlPenceresi, setGeriAlPenceresi] = useState<number | null>(null);
  /**
   * BELGE AKIŞI — az önce sayfası yüklenen ya da kâğıdı geçersiz işaretlenen
   * adım (iş emri / izin). O adım açık kalır, sıradaki sayfa öne çıkar.
   *
   * ⚠️ NEDEN (09.10.2026, Akçansa): kamera tek kare çekiyor; kare yüklenince
   *    adım tamamlanıp kapanıyor ve sıradaki adım açılıyordu (`_curKey`). İkinci
   *    sayfanın yolu kapanmış adımın içinde küçük bir "Sayfa ekle"ydi. Akçansa'nın
   *    izni beş sayfa; 06.09'dan 08.10'a yüklenen 54 izin sayfasından yalnız bir
   *    iş birden çok sayfa taşıyordu. Saha kâğıdın ilk sayfasını çekip geçiyordu.
   */
  const [belgeAkisi, setBelgeAkisi] = useState<{
    adim: 'isemri' | 'izin';
    /** Geçersiz işaretlenen foto — doğrusu bekleniyor. Yoksa sayfa ekleniyor. */
    gecersizNo?: number;
  } | null>(null);
  const { cevrimici, tazele: senkronTazele } = useSenkron();

  const { ben } = useOturum();

  const yukle = useCallback(async () => {
    try {
      setD(await api.get<Detay>(`/isler/${id}`));
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Yüklenemedi');
    }
  }, [id]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  // Onay no müşteriden onayla birlikte gelir; kayıtlıysa panel açılırken önden dolu.
  useEffect(() => {
    if (d?.iskeleOnayNo) setOnayNo((ö) => ö || d.iskeleOnayNo!);
  }, [d?.iskeleOnayNo]);

  const geriAl = async () => {
    setHata(null);
    setMesgul(true);
    try {
      // ⚠️ Kuyruğa ALINMAZ: geri alma sunucudaki SON kayda bakar, çevrimdışıyken
      //    hangi kaydın son olduğu bilinemez. Bağlantı yoksa kullanıcı bunu görür.
      await api.post(`/isler/${id}/geri-al`, {
        clientCommandId: crypto.randomUUID(),
        olayZamani: new Date().toISOString(),
      });
      setGeriAlPenceresi(null);
      await yukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Geri alınamadı');
    } finally {
      setMesgul(false);
    }
  };

  const cagir = async (yol: string, govde: Record<string, unknown> = {}) => {
    setHata(null);
    setMesgul(true);
    try {
      const r = await sahaKomutu(`/isler/${id}/${yol}`, {
        clientCommandId: crypto.randomUUID(),
        olayZamani: new Date().toISOString(),
        ...govde,
      });
      if (r.kuyruga) await senkronTazele();
      await yukle();
      setElleAcilan(null);
      setOverrideAcik(false);
      // Prototip `toastU`: geri alınabilir tikten sonra kısa süre "Geri Al" çıkar.
      if (GERI_ALINABILIR_YOL.includes(yol)) setGeriAlPenceresi(Date.now());
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'İşlem başarısız');
    } finally {
      setMesgul(false);
    }
  };

  if (hata && !d)
    return <p className="m-4 rounded-(--radius-mod) bg-crit/10 p-4 text-sm text-crit">{hata}</p>;
  if (!d) return <p className="m-4 text-sm text-wait">Yükleniyor…</p>;

  /** Son tikten sonra kısa bir pencere — prototipteki geri alınabilir toast. */
  const geriAlGorunur =
    geriAlPenceresi != null && Date.now() - geriAlPenceresi < GERI_AL_GORUNME_MS;

  const kurulum = d.fazlar.find((f) => f.tip === 'kurulum');
  /*
   * ⚠️ `iade` DIŞLANMAZ. Söküm fazının durumları: basla → devam → temiz → iade.
   *    `iade` bitiş DEĞİL, "alan temizlendi, malzeme akıbeti bekleniyor"
   *    demektir; kapanış `sokum-kapat` ile olur ve işi `kapali` yapar.
   *
   *    Önceki hâlde `iade` dışlanıyordu: ekip "temizlik tamam" der demez söküm
   *    akordeonu KAYBOLUYOR, ekran kurulum adımlarına düşüyordu. Malzeme
   *    akıbeti düğmelerine (depoya iade / bölgede bırak) hiç ulaşılamıyor ve
   *    iş `sokum` durumunda SONSUZA KADAR takılı kalıyordu.
   *
   *    Doğru ölçüt işin kendi durumu: iş `sokum` ise söküm akordeonu gösterilir.
   */
  const sokum = d.fazlar.filter((f) => f.tip === 'sokum').at(-1);
  const revize = d.fazlar.find((f) => f.tip === 'revize' && f.durum !== 'onay');

  // Prototip `mSteps`: iş fazına göre ÜÇ FARKLI adım seti üretilir.
  if (d.durum === 'sokum' && sokum) {
    return (
      <SokumAdimlari
        d={d}
        faz={sokum}
        id={id}
        cagir={cagir}
        yukle={yukle}
        mesgul={mesgul}
        hata={hata}
        onGeri={onGeri}
        geriEtiket={geriEtiket}
        cevrimici={cevrimici}
      />
    );
  }
  if (d.durum === 'revize' && revize) {
    return (
      <RevizeAdimlari
        d={d}
        faz={revize}
        id={id}
        sefYetkisi={!!ben?.sefYetkisi}
        cagir={cagir}
        yukle={yukle}
        mesgul={mesgul}
        hata={hata}
        onGeri={onGeri}
        geriEtiket={geriEtiket}
        cevrimici={cevrimici}
      />
    );
  }

  const kesifTamam = d.olculer.length > 0 || d.durum !== 'giris';
  // Dış proje bölgesinde izin sorusu yoktur (SOCAR cevap 5); adım tamam sayılır.
  const izinTamam = d.izin != null || !!d.izinGerekmez;
  const malzemeTamam = d.malzemeSevk;
  /*
   * ⚠️ İZİN ARTIK KURULUMU KİLİTLEMİYOR (03.08.2026). Önce `izinTamam &&
   *    malzemeTamam` aranıyordu; izin cevapsızken kurulum adımı ASMA KİLİTLİ
   *    kalıyordu ve kullanıcı bunu "izin fotoğrafı yüzünden" sanıyordu.
   *    Müşteri kararı: izin cevapsız kalabilir, tek kapı teslimde.
   */
  const hazir = malzemeTamam;
  const basladi = !!kurulum?.basladiAt;
  const kuruldu = !!kurulum?.bittiAt;

  const durumlar: Record<string, Durum> = {
    isemri: d.isEmriFotoVar ? 'done' : 'current',
    kesif: kesifTamam ? 'done' : 'current',
    izin: !kesifTamam ? 'locked' : izinTamam ? 'done' : 'current',
    malzeme: !kesifTamam ? 'locked' : malzemeTamam ? 'done' : 'current',
    kurulum: !hazir ? 'locked' : basladi ? 'done' : 'current',
    kurdum: !basladi ? 'locked' : kuruldu ? 'done' : 'current',
    /*
     * ⚠️ ÖLÇÜT `teslimAt`, işin ANLIK DURUMU DEĞİL.
     *
     *    Önceden `d.durum === 'ayakta'` bakılıyordu: iş teslim edilip sonra
     *    söküme girince ya da kapanınca durum artık `ayakta` olmadığı için
     *    teslim adımı GERİ "bekliyor"a dönüyordu. Sökümü bitmiş, kapanmış bir
     *    işte ekran *"Kurdum kaydedildi — şef onayına gitti"* diyordu; şef de
     *    haklı olarak "bunu onaylamam gerekmiyor mu?" diye soruyordu.
     *
     *    Teslim BİR KEZ olur ve geri alınamaz (K2 geçidi geçilmiş, müşteriye
     *    mail gitmiş, yeşil kart asılmış). Damgası varsa o adım tamamdır.
     */
    onay: !kuruldu ? 'locked' : d.teslimAt ? 'done' : 'current',
  };

  // Prototip `_curKey`: ilk `current` adım otomatik AÇIK.
  const sirada =
    ['kesif', 'izin', 'malzeme', 'kurulum', 'kurdum', 'onay', 'isemri'].find(
      (k) => durumlar[k] === 'current',
    ) ?? null;
  const acik = elleAcilan === '__kapali__' ? null : (elleAcilan ?? sirada);
  const ac = (k: string) => {
    setBelgeAkisi(null);
    setElleAcilan(acik === k ? '__kapali__' : k);
  };
  /** Sayfa yüklendi: adım açık kalır, "Sonraki sayfayı çek" öne çıkar. */
  const sayfaYuklendi = (adim: 'isemri' | 'izin') => {
    setBelgeAkisi({ adim });
    setElleAcilan(adim);
  };
  /** "Tüm sayfalar yüklendi" — sıradaki adım yine kendiliğinden açılır. */
  const belgeBitti = () => {
    setBelgeAkisi(null);
    setElleAcilan(null);
  };
  /** Adımın SAYILAN sayfaları — geçersiz işaretlenen kâğıt sayılmaz (K3/K5). */
  const gecerliSayfa = (step: string) =>
    d.fotolar.filter((f) => f.step === step && !f.gecersiz).length;

  /*
   * DUZELTME KAPISI — kural `@mod/domain`den. Client butonu sondurur,
   * server istegi reddeder; ayni kural iki yerde AYRI YAZILMAZ (CLAUDE.md).
   */
  const duzeltGate = (alan: DuzeltmeAlani) => {
    if (!ben) return undefined;
    const izin = duzeltilebilirMi({ durum: d.durum }, alan, {
      id: ben.id,
      siteId: ben.santiye.siteId,
      role: ben.rol,
      sistemRol: ben.sistemRol,
    });
    return izin.ok ? () => setDuzeltAdim(alan) : undefined;
  };

  /**
   * "BU YANLIŞ FOTOĞRAF" — şef/mühendis/formen için, kapanmamış işte.
   *
   * ⚠️ SAHADA DA OLMALI: formen bir saha rolü ve yanlış kâğıdı fark eden ilk
   *    kişi genelde o. Yalnız masaüstünde olsaydı, düzeltme asıl kullanıcısına
   *    hiç ulaşmazdı — adım düzeltmesinde de aynı gerekçe var.
   */
  const gecersizIsle =
    ben &&
    fotoGecersizIsaretlenebilirMi({ durum: d.durum }, { gecersiz: false }, {
      id: ben.id,
      siteId: ben.santiye.siteId,
      role: ben.rol,
      sistemRol: ben.sistemRol,
    }).ok
      ? async (fotoNo: number, gerekce: string) => {
          await api.post(`/fotolar/${fotoNo}/gecersiz`, { gerekce });
          await yukle();
          /*
           * Kâğıt adımında işaretleme bir "değiştir"in ilk yarısıdır: adım açık
           * kalır ve doğrusunu çekecek düğme öne çıkar. Öteki adımlarda (keşif
           * gibi) sonradan yükleme yolu yok; orada yalnız işaretlenir.
           */
          const step = d.fotolar.find((f) => f.fotoNo === fotoNo)?.step;
          const adim = step === 'is_emri' ? 'isemri' : step === 'izin' ? 'izin' : null;
          if (adim) {
            setBelgeAkisi({ adim, gecersizNo: fotoNo });
            setElleAcilan(adim);
          }
        }
      : undefined;
  /** Künyede ünitenin yanındaki "düzelt" — adım düzeltmesiyle aynı kapı. */
  const uniteDuzelt = duzeltGate('isEmri');

  return (
    <div className="mx-auto max-w-lg p-4">
      <GeriDugme etiket={geriEtiket} onClick={onGeri} />
      <div className="mb-4">
        <p className="num text-sm text-wait">{d.isEmriNo ?? '—'}</p>
        <h1 className="text-base font-semibold text-navy">{d.tanim}</h1>
        <p className="text-xs text-wait">
          {d.unite}
          {/*
            ⚠️ ÜNİTE DÜZELTMESİ BURADAN DA AÇILIR (09.10.2026, Akçansa). Yol
               "İş emri" adımının ⋮ menüsündeydi ve 06.09'dan beri hiç
               kullanılmadı; yanlış üniteyi gören kişi künyeye bakıyor.
               Aynı form, aynı kapı (`duzeltilebilirMi`).
          */}
          {uniteDuzelt && (
            <>
              {' · '}
              <button type="button" onClick={uniteDuzelt} className="text-navy underline">
                düzelt
              </button>
            </>
          )}
        </p>
        <SapSatiri d={d} />
      </div>

      {hata && (
        <p className="mb-3 rounded-(--radius-mod) bg-crit/10 p-3 text-sm text-crit">{hata}</p>
      )}

      {/*
        GERİ AL — prototipteki geri alınabilir bildirim (`toastU`/`doUndo`).
        Eldivenli, aceleci kullanıcı yanlış tikliyor; geri alma yolu yoksa tek
        çare şefi aramak olur. Silme DEĞİL: iz kaydına "↩ geri alındı" satırı
        eklenir, önceki tik yerinde kalır (T4).
      */}
      {geriAlGorunur && (
        <div className="mb-3 flex items-center gap-3 rounded-(--radius-mod) bg-navy px-3 py-2.5 text-sm text-white">
          <span className="flex-1">Kaydedildi.</span>
          <button
            onClick={() => void geriAl()}
            disabled={mesgul}
            className="font-medium underline disabled:opacity-40"
          >
            ↩ Geri Al
          </button>
          <button onClick={() => setGeriAlPenceresi(null)} aria-label="Kapat" className="opacity-60">
            <KapatIkon boyut={16} />
          </button>
        </div>
      )}

      {duzeltAdim && (
        <div className="mb-3">
          <AdimDuzelt
            d={d}
            adim={duzeltAdim}
            onKapat={() => setDuzeltAdim(null)}
            onBitti={() => {
              setDuzeltAdim(null);
              void yukle();
            }}
          />
        </div>
      )}

      <div className="space-y-2">
        {/* İş emri — her akışta ÜSTTE (v7): usta hangi işi yaptığını kâğıttan teyit eder */}
        <Adim
          durum={durumlar.isemri!}
          gate
          baslik="İş emri"
          alt={d.isEmriFotoVar ? 'Kâğıt görüntüsü yüklü' : '⚠ Kâğıt fotosu eksik (todo)'}
          acik={acik === 'isemri'}
          onAc={() => ac('isemri')}
          onDuzelt={duzeltGate('isEmri')}
        >
          {belgeAkisi?.adim === 'isemri' ? (
            <div className="space-y-2">
              <FotoSerit
                fotolar={d.fotolar.filter((f) => f.step === 'is_emri')}
                boyut={72}
                onGecersiz={gecersizIsle}
                eylem="yanlışsa değiştir"
              />
              <BelgeAkisi
                jobId={id}
                step="is_emri"
                gecerliSayfa={gecerliSayfa('is_emri')}
                gecersizNo={belgeAkisi.gecersizNo}
                onYuklendi={async () => {
                  await yukle();
                  sayfaYuklendi('isemri');
                }}
                onBitti={belgeBitti}
              />
            </div>
          ) : d.isEmriFotoVar ? (
            /*
             * ⚠️ FOTO VARKEN DE YÜKLEME YOLU AÇIK KALIR (06.09.2026, Akçansa).
             *    Önceden ilk sayfa yüklendiği an düğme ekrandan kalkıyordu ve
             *    kullanıcının gördüğü şey şuydu: *"iş emri yüklerken sadece 1
             *    sayfa yüklenebiliyor, bugün 2 sayfa olarak geldi."* Engel veri
             *    modelinde değildi (bir adım her zaman çok fotoğraf taşıyabilir),
             *    yalnız ekranda ikinci sayfaya giden kapı yoktu.
             */
            <div className="space-y-2">
              <FotoSerit
                fotolar={d.fotolar.filter((f) => f.step === 'is_emri')}
                boyut={72}
                onGecersiz={gecersizIsle}
                eylem="yanlışsa değiştir"
              />
              <FotoEkle
                jobId={id}
                step="is_emri"
                etiket="Sayfa ekle"
                gorunum="kompakt"
                onBitti={async () => {
                  await yukle();
                  sayfaYuklendi('isemri');
                }}
              />
            </div>
          ) : (
            <>
              {/* Geçersiz işaretlenen kâğıt burada da görünür kalır — silinmedi, sayılmıyor. */}
              {d.fotolar.some((f) => f.step === 'is_emri') && (
                <div className="mb-2">
                  <FotoSerit
                    fotolar={d.fotolar.filter((f) => f.step === 'is_emri')}
                    boyut={72}
                    onGecersiz={gecersizIsle}
                    eylem="yanlışsa değiştir"
                  />
                </div>
              )}
              <Bilgi>
                Hangi işi yaptığını teyit için iş emri kâğıdını yükle. İş bu foto olmadan kapanamaz.
                Kâğıt birden fazla sayfaysa ilk sayfadan sonra sıradakini çek, ya da galeriden
                hepsini seç.
              </Bilgi>
              <FotoEkle
                jobId={id}
                step="is_emri"
                etiket="İş emri kâğıdını fotoğrafla"
                onBitti={async () => {
                  await yukle();
                  sayfaYuklendi('isemri');
                }}
              />
            </>
          )}
        </Adim>

        <Adim
          durum={durumlar.kesif!}
          baslik="Keşif + kurulum ekibi"
          alt={
            kesifTamam
              ? `Metraj ${metrajGoster(d)}${d.olculer.length > 1 ? ` (${d.olculer.length} parça)` : ''} · ${kurulum?.ekip.join(', ') || 'ekip yok'}`
              : 'Ölçü miksi → metraj, sonra ekibi seç'
          }
          acik={acik === 'kesif'}
          onAc={() => ac('kesif')}
          onDuzelt={duzeltGate('kesif')}
        >
          {kesifTamam ? (
            <div className="space-y-1 text-sm">
              <OlcuListesi olculer={d.olculer} m3={d.metrajM3} m2={d.metrajM2} kucuk />
              {d.kesifNot && <p className="text-xs text-ink-2"><NotIkon boyut={12} className="inline" /> {d.kesifNot}</p>}
              <p className="text-xs text-wait"><EkipIkon boyut={12} className="inline" /> {kurulum?.ekip.join(', ') || '—'}</p>
              {d.fotolar.filter((f) => f.step === 'kesif').length > 0 && (
                <div className="pt-1">
                  <p className="mb-1 text-xs text-wait">Keşif fotoğrafları</p>
                  <div className="flex justify-start">
                    <FotoSerit
                    fotolar={d.fotolar.filter((f) => f.step === 'kesif')}
                    onGecersiz={gecersizIsle}
                  />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <KesifForm jobId={id} onBitti={yukle} />
          )}
        </Adim>

        {/* K4 — izin ve malzeme EŞ ZAMANLI, ayrı adımlar; biri diğerini kilitlemez */}
        <Adim
          durum={durumlar.izin!}
          baslik="İş izni — imzalı kâğıt"
          alt={
            d.izinGerekmez
              ? 'Bu bölgede iş izni gerekmiyor'
              : d.izin === 'override'
              ? '⚠ Override: izinsiz başlatıldı'
              : d.izin === 'alindi'
                ? 'İzin kaydedildi'
                : 'Müşteri imzalı kâğıdı çek — teslime kadar erteleyebilirsin'
          }
          acik={acik === 'izin'}
          onAc={() => ac('izin')}
          onDuzelt={duzeltGate('izinMalzeme')}
        >
          {d.izinGerekmez ? (
            <Bilgi>Bu bölge dış proje: iş izni kâğıdı istenmez, teslim de onu beklemez.</Bilgi>
          ) : izinTamam ? (
            <div className="space-y-2">
              <p className="text-sm text-wait">
                {d.izin === 'override'
                  ? `İzin alınamadı — sorumluluk tıklayanda${d.izinNotu ? `: ${d.izinNotu}` : ''}`
                  : 'İzin kaydedildi'}
              </p>
              <FotoSerit
                fotolar={d.fotolar.filter((f) => f.step === 'izin')}
                onGecersiz={gecersizIsle}
                eylem="yanlışsa değiştir"
              />
              {belgeAkisi?.adim === 'izin' ? (
                <BelgeAkisi
                  jobId={id}
                  step="izin"
                  gecerliSayfa={gecerliSayfa('izin')}
                  gecersizNo={belgeAkisi.gecersizNo}
                  onYuklendi={async () => {
                    await yukle();
                    sayfaYuklendi('izin');
                  }}
                  onBitti={belgeBitti}
                />
              ) : (
                <>
                  {/*
                    SONRADAN YÜKLEME YOLU. Fotoğraf ertelenebiliyor ama kâğıt
                    SAHADA — yükleyecek kişi burada. Bu yol olmasaydı erteleyen
                    usta kâğıdı bir daha hiç yükleyemez, iş teslimde takılırdı (K5).
                  */}
                  {!k5_izinFotosuVarMi({
                    izin: d.izin,
                    izinGerekmez: d.izinGerekmez,
                    photos: d.fotolar.map((f) => ({
                      step: f.step as PhotoStep,
                      phaseType: 'kurulum' as const,
                      cekilmeZamani: new Date(),
                      // Geçersiz işaretli kâğıt K5 kapısını açmaz — sunucuyla aynı okuma.
                      gecersiz: f.gecersiz,
                    })),
                  }).ok && (
                    <div className="rounded-(--radius-kart) border border-warn/40 bg-warn/5 p-3">
                      <p className="mb-2 text-xs text-ink-2">
                        Kâğıdın fotoğrafı henüz yok — <strong>iş teslim edilemez</strong>. Elindeyse
                        şimdi çek.
                      </p>
                      <FotoEkle
                        jobId={id}
                        step="izin"
                        etiket="İzin kâğıdını çek"
                        onBitti={async () => {
                          await yukle();
                          sayfaYuklendi('izin');
                        }}
                      />
                    </div>
                  )}
                  {/*
                    ⚠️ FOTO VARKEN DE SAYFA EKLENEBİLİR (06.09.2026, Akçansa).
                       Üstteki kutu YALNIZ K5 kapısı hâlâ açıkken çiziliyor; ilk
                       sayfa yüklenir yüklenmez kapanıyordu. Akçansa'da bir iş izni
                       BEŞ SAYFA ve kullanıcı ikinci sayfayı yükleyecek yer
                       bulamadı. K5 tek sayfayla tatmin olur, belge olmaz.
                  */}
                  {d.fotolar.some((f) => f.step === 'izin') && (
                    <FotoEkle
                      jobId={id}
                      step="izin"
                      etiket="Sayfa ekle"
                      gorunum="kompakt"
                      onBitti={async () => {
                        await yukle();
                        sayfaYuklendi('izin');
                      }}
                    />
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              {/*
                ⚠️ ETİKET "ÇEK", "çok sayfa / PDF" DEĞİL. Telefonda bu düğme kamerayı
                   açar ve tek kare çeker; eski etiket çoklu seçim vaat ediyordu
                   (o yalnız alttaki "Galeriden seç"te var). Çok sayfa artık
                   ilk kareden sonra açık kalan adımdan geliyor (`belgeAkisi`).
              */}
              <FotoEkle
                jobId={id}
                step="izin"
                etiket="İzin kâğıdını çek"
                onBitti={async () => {
                  await cagir('izin', { durum: 'alindi' });
                  sayfaYuklendi('izin');
                }}
              />
              {/* Fotoğraf ERTELENEBİLİR — kapı TESLİMDE (K5), kurulumda değil. */}
              <button
                onClick={() => void cagir('izin', { durum: 'alindi' })}
                className="mt-3 block w-full text-center text-xs text-muted underline"
              >
                İzin alındı, fotoğrafı sonra yükleyeceğim
              </button>
              {/* K5 — override BUTON DEĞİL, altta duran LİNK. Kazara basılmasın. */}
              {!overrideAcik ? (
                <button
                  onClick={() => setOverrideAcik(true)}
                  className="mt-3 block w-full text-center text-xs text-warn underline"
                >
                  İzni alamadım — yine de başlıyorum (kayıt düşülür)
                </button>
              ) : (
                <div className="mt-3 space-y-2">
                  <input
                    className="w-full rounded-(--radius-mod) border border-warn px-3 py-2 text-sm"
                    placeholder="Neden izin alınamadı?"
                    value={overrideNot}
                    onChange={(e) => setOverrideNot(e.target.value)}
                  />
                  <button
                    disabled={mesgul || overrideNot.trim().length < 3}
                    onClick={() => void cagir('izin', { durum: 'override', overrideNotu: overrideNot })}
                    className="w-full rounded-(--radius-mod) bg-warn px-4 py-2.5 text-sm text-white disabled:opacity-40"
                  >
                    İzinsiz devam et — sorumluluk bende
                  </button>
                </div>
              )}
            </>
          )}
        </Adim>

        <Adim
          durum={durumlar.malzeme!}
          baslik="Malzeme hazır ve sevk edildi"
          alt={malzemeTamam ? 'Hazırlandı & çalışma alanına götürüldü' : 'Şantiye deposundan hazırla'}
          acik={acik === 'malzeme'}
          onAc={() => ac('malzeme')}
          onDuzelt={duzeltGate('izinMalzeme')}
        >
          {malzemeTamam ? (
            <p className="text-sm text-wait">Malzeme sevk edildi</p>
          ) : (
            <>
              {/* Faz 5 kararı: "Hazırlanacaklar" tüm Nuh'a açık, herkes işaretleyebilir */}
              <Bilgi>Hazırlanacakları herkes yapabilir. Sen de buradan işaretleyebilirsin.</Bilgi>
              <Buton disabled={mesgul} onClick={() => void cagir('malzeme')}>
                <OnayIkon boyut={16} /> Hazır &amp; sevk et
              </Buton>
            </>
          )}
        </Adim>

        <Adim
          durum={durumlar.kurulum!}
          baslik="Kurulum başladı"
          /*
            ⚠️ KİLİTLİ ADIM NEDENİNİ SÖYLEMELİ. Önceden yalnız "Kurulumu başlat"
               yazıyordu; kullanıcı asma kilidi görüp NEYİN eksik olduğunu
               tahmin etmek zorunda kalıyor ve yanlış tahmin ediyordu — sahada
               "izin fotoğrafı yüzünden kilitli" sanıldı. İzin artık kurulumu
               hiç kilitlemiyor; tek kapı teslimde.
          */
          alt={
            basladi
              ? `Başladı ${new Date(kurulum!.basladiAt!).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`
              : !malzemeTamam
                ? 'Önce malzeme sevkini işaretle — izin şart değil'
                : 'Kurulumu başlat (zaman damgası)'
          }
          acik={acik === 'kurulum'}
          onAc={() => ac('kurulum')}
          onDuzelt={duzeltGate('ekip')}
        >
          {basladi ? (
            <p className="num text-sm text-wait">
              {new Date(kurulum!.basladiAt!).toLocaleString('tr-TR')}
            </p>
          ) : (
            <Buton disabled={mesgul} onClick={() => void cagir('kurulum-basla', { overrideOnayi: false })}>
              ✓ Kurulumu Başlat{!cevrimici && ' (kuyruğa)'}
            </Buton>
          )}
        </Adim>

        <Adim
          durum={durumlar.kurdum!}
          baslik='"Ben Kurdum"'
          alt={kuruldu ? `Kuruldu — şefe onaya gitti · ${kurulum?.ekip.length ?? 0} kişi` : 'Bitince bas — şef kontrole çağrılır'}
          acik={acik === 'kurdum'}
          onAc={() => ac('kurdum')}
        >
          {kuruldu ? (
            <p className="text-sm text-wait">Şef onayına gitti.</p>
          ) : (
            <>
              {/* v7: "kaç kişi çalıştınız?" SORULMAZ — ama ne varsayıldığı GÖSTERİLİR */}
              <Bilgi>
                Kaç kişi çalıştığı ekipten geliyor: <strong>{kurulum?.ekip.length ?? 0} kişi</strong>
                {kurulum?.ekip.length ? ` (${kurulum.ekip.join(', ')})` : ''}.
              </Bilgi>
              <Buton disabled={mesgul} onClick={() => void cagir('kurulum-bitti')}>
                ✓ BEN KURDUM{!cevrimici && ' (kuyruğa)'}
              </Buton>
            </>
          )}
        </Adim>

        {/*
          KONTROL & TESLİM.

          ⚠️ ŞEF MOBİLDE DE TESLİM EDER (kullanıcı kararı 04.08.2026). Önceden bu
          adım telefonda YALNIZ bilgi kartıydı ("sende işlem yok") — v9'un "şefin
          mobili yalnız saha yürütmesi" varsayımına dayanıyordu. Ama şef sahada
          telefonuyla kontrol fotoğraflarını yükleyip onaylıyor (§3.1). Panel
          masaüstündeki IsDetay/KontrolOnay ile AYNI kuralları kullanır — iki ekran
          ayrışmasın diye engel listesi de teslim çağrısı da birebir aynıdır.

          Saha rolü (usta/formen) için adım hâlâ bilgilendirmedir: onay şefin/mühendisin.
        */}
        {ben?.sefYetkisi && d.durum === 'kontrol' ? (
          <Adim
            durum={durumlar.onay!}
            gate
            baslik="Kontrol & teslim"
            alt="Fotoğrafları yükle, onay no'yu gir, teslim et"
            acik={acik === 'onay'}
            onAc={() => ac('onay')}
          >
            <TeslimPaneli
              d={d}
              id={id}
              onayNo={onayNo}
              setOnayNo={setOnayNo}
              mesgul={mesgul}
              yukle={yukle}
              onOlcuEkle={duzeltGate('kesif')}
              manuel={manuel}
              setManuel={setManuel}
              onTeslim={() =>
                void cagir('teslim', {
                  nihaiOlculer: nihaiOlculer(d.olculer, manuel),
                  iskeleOnayNo: onayNo.trim(),
                })
              }
            />
          </Adim>
        ) : (
          <Adim
            durum={durumlar.onay!}
            gate
            baslik="Kontrol & teslim (şef/mühendis)"
            alt={
              d.durum === 'ayakta'
                ? 'Onaylandı — müşteriye mail gitti'
                : d.durum === 'kontrol'
                  ? 'Şef onay + fotoğraflar (sende işlem yok)'
                  : 'Kurulumdan sonra'
            }
            acik={acik === 'onay'}
            onAc={() => ac('onay')}
          >
            {/*
              ⚠️ TESLİM EDİLMİŞ İŞTE KONTROL FOTOĞRAFLARI GÖSTERİLİR
                 (bildirim 11.08.2026: "kontrol aşaması tamamlanmış işin kontrol
                 görselleri görünmüyor").

              Bu dal hem saha rolü için hem de TESLİMDEN SONRA şef için
              çiziliyordu ve içinde yalnız "sende işlem yok" metni vardı. Yani
              iş kapandığı anda yeşil kart, kontrol formu ve kurulu iskele
              fotoğrafları telefonda GÖRÜNMEZ oluyordu — kanıt zinciri duruyor
              ama sahadaki kişi bakamıyor. Masaüstünde görünüyorlardı; ekranlar
              yine ayrışmıştı.
            */}
            {d.teslimAt ? (
              <KontrolGorselleri d={d} />
            ) : (
              <Bilgi>
                "Kurdum" kaydedildi — şef onayına gitti. Onay ve fotoğraflar şefte.
                <br />
                <span className="text-xs">
                  Kuran kişi kendi işini onaylayamaz (K1, regülasyon).
                </span>
              </Bilgi>
            )}
          </Adim>
        )}

        {/*
          FAZ AKSİYONLARI — söküm emri aç · revize aç · iş kapat.

          ⚠️ 07.08.2026'ya kadar bu üç eylem YALNIZ masaüstündeydi; şef
             telefondayken söküm talimatı OLUŞTURAMIYORDU (müşteri bildirimi).
             Sunucu tarafı (`sokumAc`) hazırdı, eksik olan yoldu — dünkü
             "şef mobilden onay veremiyor" sorununun aynı deseni: panel var,
             ona giden yol yok.

          ⚠️ Görünürlük kuralı masaüstüyle AYNI (§6.3): yalnız iskele `ayakta`
             iken ve yalnız şef yetkisi olanda. Devam eden bir faz varken
             ikinci faz açılamaz — sunucu da reddediyor (`acilabilirMi`).
        */}
        {d.durum === 'ayakta' && ben?.sefYetkisi && (
          <div className="mt-4">
            <FazAksiyon jobId={id} oncekiEkip={kurulum?.ekip ?? []} onBitti={yukle} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ADIM KARTI — prototipteki `.mstep`.
 *
 * ⚠️ Durum RENKLE de anlatılır, yalnız ikonla değil: `done` yeşil zemin +
 *    yeşil çerçeve + beyaz-üstü-yeşil ikon kutusu. Tek renkli düz satırlarda
 *    saha kullanıcısı listeyi TARAYARAK nerede kaldığını göremiyordu; kart
 *    zemini bunu bir bakışta veriyor.
 *
 * ⚠️ `gate` (sert geçit) kırmızı ÇERÇEVE alır, kırmızı zemin değil — geçit
 *    "dikkat et" demek, "hata var" demek değil.
 */
function Adim({
  durum,
  gate,
  baslik,
  alt,
  ikon,
  acik,
  onAc,
  onDuzelt,
  children,
}: {
  durum: Durum;
  gate?: boolean;
  baslik: string;
  alt: string;
  /** Adıma özgü ikon; verilmezse duruma göre ✓ / ● / kilit çizilir. */
  ikon?: ReactNode;
  acik: boolean;
  onAc: () => void;
  /**
   * Bu adımda girilen değerleri düzelt. Verilmezse çizilmez.
   *
   * ⚠️ Telefonda ⋮ DEĞİL, altta duran tam genişlikte LİNK — eldivenli parmak
   *    3 mm'lik bir ikonu ıskalar, ve kazara basılırsa form açılır (zararsız).
   */
  onDuzelt?: () => void;
  children: React.ReactNode;
}) {
  const kilitli = durum === 'locked';
  const bitti = durum === 'done';

  const kartStil = kilitli
    ? 'border-line bg-surface-2 opacity-60'
    : bitti
      ? 'border-ok-line bg-ok-bg'
      : gate
        ? 'border-crit-line bg-surface'
        : 'border-line bg-surface';

  const kutuStil = kilitli
    ? 'bg-line text-muted'
    : bitti
      ? 'bg-ok text-white'
      : gate
        ? 'bg-crit-bg text-crit'
        : 'bg-navy-bg text-navy';

  return (
    <div className={`overflow-hidden rounded-(--radius-kart) border ${kartStil}`}>
      <button
        onClick={kilitli ? undefined : onAc}
        disabled={kilitli}
        className={`flex w-full items-center gap-3 p-3.5 text-left ${
          kilitli ? 'cursor-default' : 'active:bg-black/[.03]'
        }`}
      >
        <span
          className={`flex h-9.5 w-9.5 shrink-0 items-center justify-center rounded-[11px] ${kutuStil}`}
        >
          {ikon ?? (bitti ? <OnayIkon boyut={19} /> : kilitli ? <KilitIkon boyut={19} /> : <NoktaIkon boyut={19} />)}
        </span>

        <span className="min-w-0 flex-1">
          <span className={`block text-sm leading-tight font-bold ${bitti ? 'text-ok' : 'text-ink'}`}>
            {baslik}
          </span>
          <span className="mt-0.5 block text-[11px] leading-snug text-muted">{alt}</span>
        </span>

        <span className="flex shrink-0 flex-col items-end gap-1">
          {bitti && (
            <span className="flex items-center gap-1 text-[11px] font-semibold text-ok">
              <OnayIkon boyut={12} /> TAMAM
            </span>
          )}
          {gate && !bitti && (
            <span className="text-[10px] font-semibold tracking-wide text-crit uppercase">
              sert geçit
            </span>
          )}
          {!kilitli && <OkAsagiIkon boyut={15} className={`text-faint ${acik ? 'rotate-180' : ''}`} />}
        </span>
      </button>

      {acik && !kilitli && (
        <div className="border-t border-dashed border-line px-3.5 pt-3 pb-4">
          {children}
          {onDuzelt && (
            <button
              onClick={onDuzelt}
              className="mt-3 block w-full rounded-(--radius-mod) border border-line-2 py-2 text-center text-xs text-muted active:bg-black/[.03]"
            >
              Bu adımda girilenleri düzelt
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * TESLİM EDİLMİŞ İŞİN KONTROL FOTOĞRAFLARI — salt görünüm.
 *
 * ⚠️ YÜKLEME DÜĞMESİ YOK, bilinçli: iş teslim edilmiştir, K2 geçidi geçilmiş,
 *    müşteriye mail gitmiştir. Buraya yeni fotoğraf eklemek kanıt zincirine
 *    sonradan ekleme yapmak olurdu; düzeltme yolu şefin "⋮ düzelt" akışıdır.
 *
 * Boş yuva da GÖSTERİLİR ("—"): kontrol formu opsiyonel (K2 04.08'de daraltıldı)
 * ve "yok" bilgisi de bilgidir — gizlenirse eksikliği kimse fark etmez.
 */
function KontrolGorselleri({
  d,
}: {
  d: {
    fotolar: {
    fotoNo: number;
    step: string;
    contentType?: string | null;
    /** Yanlış yüklenen kâğıt — kayıt durur, sayılmaz (K8). */
    gecersiz?: boolean;
    gecersizNeden?: string | null;
  }[];
    teslimAt: string | null;
    iskeleOnayNo: string | null;
  };
}) {
  const yuvalar = [
    { step: 'kontrol_formu', ad: 'Kontrol formu' },
    { step: 'yesil_kart', ad: 'Yeşil kart' },
    { step: 'kurulu_iskele', ad: 'Kurulu iskele' },
  ];

  return (
    <div className="space-y-2">
      <p className="text-xs leading-relaxed text-ok">
        ✓ Kontrol edildi, teslim edildi
        {d.teslimAt && ` · ${new Date(d.teslimAt).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}`}
        {d.iskeleOnayNo && (
          <>
            {' · onay no '}
            <span className="num">{d.iskeleOnayNo}</span>
          </>
        )}
      </p>
      {yuvalar.map((y) => {
        const f = d.fotolar.filter((x) => x.step === y.step);
        return (
          <div key={y.step} className="rounded-(--radius-mod) border border-line p-2.5">
            <p className="mb-1.5 text-sm font-medium text-ink-2">{y.ad}</p>
            {f.length > 0 ? (
              <FotoSerit fotolar={f} />
            ) : (
              <p className="text-xs text-wait">— fotoğraf yok —</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** SAP iş emri alanları — boşsa hiç çizilmez (Nuh, Akçansa). */
function SapSatiri({ d }: { d: Detay }) {
  if (!d.sorumluIsyeri && !d.planlamaGrubu && !d.oncelik) return null;
  return (
    <p className="text-xs text-wait">
      Sorumlu işyeri <span className="num">{d.sorumluIsyeri ?? '—'}</span> · Planlama grubu{' '}
      <span className="num">{d.planlamaGrubu ?? '—'}</span> · Öncelik{' '}
      <span className="num">{d.oncelik ?? '—'}</span>
    </p>
  );
}

/**
 * BELGE AKIŞI — çok sayfalı kâğıdın sıradaki sayfası ya da yanlış kâğıdın doğrusu.
 *
 * Sayfa yüklendikten sonra adımın içinde kalan TAM BOY düğme. Kullanıcı
 * "Tüm sayfalar yüklendi" deyince akış biter ve sıradaki adım açılır.
 * Kâğıdın kaç sayfa olduğunu sistem bilemez; bitti demek sahanın işi.
 */
function BelgeAkisi({
  jobId,
  step,
  gecerliSayfa,
  gecersizNo,
  onYuklendi,
  onBitti,
}: {
  jobId: string;
  step: 'is_emri' | 'izin';
  gecerliSayfa: number;
  gecersizNo?: number;
  onYuklendi: () => Promise<void>;
  onBitti: () => void;
}) {
  const degisim = gecersizNo != null;
  return (
    <div className="space-y-2 rounded-(--radius-kart) border border-ok-line bg-ok-bg p-3">
      <p className="text-sm text-ink-2">
        {degisim ? (
          <>
            Foto <span className="num">{gecersizNo}</span> geçersiz işaretlendi. Doğru kâğıdı çek;
            birden çok sayfaysa hepsini sırayla ekle.
          </>
        ) : gecerliSayfa > 0 ? (
          <>
            <strong className="num">{gecerliSayfa}</strong> sayfa yüklendi. Kâğıt devam ediyorsa
            sıradaki sayfayı çek.
          </>
        ) : (
          // Çevrimdışı: sayfa kuyrukta, sunucudaki listede henüz yok.
          <>Sayfa sıraya alındı, bağlantı gelince gönderilecek. Kâğıt devam ediyorsa sıradakini çek.</>
        )}
      </p>
      <FotoEkle
        jobId={jobId}
        step={step}
        etiket={degisim ? 'Doğru kâğıdı çek' : 'Sonraki sayfayı çek'}
        onBitti={onYuklendi}
      />
      <button
        type="button"
        onClick={onBitti}
        className="w-full rounded-(--radius-mod) border border-line-2 bg-surface px-4 py-2.5 text-sm font-medium text-ink-2 active:bg-ground"
      >
        {degisim ? 'Şimdi değil' : 'Tüm sayfalar yüklendi'}
      </button>
    </div>
  );
}

function Bilgi({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-3 rounded-(--radius-mod) bg-info/8 p-2.5 text-xs leading-relaxed text-ink-2">
      {children}
    </p>
  );
}

/** Saha butonu — eldivenli parmak icin BUYUK dokunma alani. */
function Buton({
  children,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-center justify-center gap-1.5 rounded-(--radius-mod) bg-navy px-4 py-3.5 text-base font-medium text-white disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/**
 * TESLİM PANELİ (mobil) — şefin sahada kontrol & onay yapabilmesi için.
 *
 * ⚠️ Masaüstündeki IsDetay/KontrolOnay ile AYNI kuralları kullanır. Engel listesi
 *    (`teslimEngelleri`) ve teslim çağrısı birebir aynıdır; kural iki ekranda
 *    ayrı yazılmaz (CLAUDE.md). Değişecekse ikisi birlikte değişmeli.
 *
 * ⚠️ K1 (kuran kişi kendi işini onaylayamaz) SUNUCUDA zorlanır. Şef bu işi kendi
 *    kurduysa "Onayla & Teslim" yine görünür ama sunucu reddeder ve gerekçesi
 *    üstteki hata şeridinde belirir — masaüstüyle aynı davranış.
 */
function TeslimPaneli({
  d,
  id,
  onayNo,
  setOnayNo,
  mesgul,
  yukle,
  onOlcuEkle,
  onTeslim,
  manuel,
  setManuel,
}: {
  d: Detay;
  id: string;
  onayNo: string;
  setOnayNo: (v: string) => void;
  mesgul: boolean;
  yukle: () => void;
  onOlcuEkle: (() => void) | undefined;
  onTeslim: () => void;
  /** `null` → keşif ölçüsü aynen. Dolu → toplam elle girildi. */
  manuel: ManuelOlcu[] | null;
  setManuel: (m: ManuelOlcu[] | null) => void;
}) {
  const foto = (s: string) => d.fotolar.filter((f) => f.step === s);
  /** Üstteki toplam bundan gelir — seçimle ANINDA değişir (masaüstüyle aynı fonksiyon). */
  const nihai = nihaiToplam(d.olculer, manuel);
  // Kontrol formu 04.08.2026'da zorunluluktan çıkarıldı (K2 daraltıldı) — yuva
  // duruyor, yüklenirse kanıt zincirine girer ama teslimi bloklamaz.
  const yuvalar = [
    { step: 'kontrol_formu', ad: 'Kontrol formu', zorunlu: false },
    { step: 'yesil_kart', ad: 'Yeşil kart', zorunlu: true },
    { step: 'kurulu_iskele', ad: 'Kurulu iskele', zorunlu: true },
  ];
  const fotolarTam = yuvalar.every((y) => !y.zorunlu || foto(y.step).length > 0);
  const izinFotosuEksik = !k5_izinFotosuVarMi({
    izin: d.izin,
    izinGerekmez: d.izinGerekmez,
    photos: d.fotolar.map((f) => ({
      step: f.step as PhotoStep,
      phaseType: 'kurulum' as const,
      cekilmeZamani: new Date(),
      gecersiz: f.gecersiz,
    })),
  }).ok;

  const teslimEngelleri: string[] = [
    !fotolarTam ? 'Yeşil kart ve kurulu iskele fotoğrafı eklenmeden teslim edilemez (K2).' : null,
    !d.isEmriFotoVar ? 'İş emri kâğıdı fotoğrafı eksik (K3).' : null,
    izinFotosuEksik ? 'İmzalı izin kâğıdı fotoğrafı eksik (K5).' : null,
    !onayNo.trim() ? 'İskele onay no girilmeli.' : null,
    // Ölçüt masaüstüyle ve sunucudaki `teslimSchema` refine'ıyla AYNI: satır
    // sayısı değil TOPLAM. Elle giren şefin satıra ihtiyacı yok.
    nihai.m3 <= 0 && nihai.m2 <= 0
      ? manuel
        ? 'Elle girilen metraj sıfır olamaz.'
        : 'Nihai ölçü yok — en az bir ölçü satırı gerekli ya da metrajı elle girin.'
      : null,
  ].filter((x): x is string => x !== null);

  return (
    <div className="space-y-3">
      <Bilgi>
        <span className="text-xs">Kuran kişi kendi işini onaylayamaz (K1, regülasyon).</span>
      </Bilgi>

      {/* Üç kontrol fotoğrafı */}
      <div className="space-y-2">
        {yuvalar.map((y) => {
          const f = foto(y.step);
          return (
            <div key={y.step} className="rounded-(--radius-mod) border border-line p-2.5">
              <p className="mb-1.5 text-sm font-medium text-ink-2">
                {y.ad}
                {!y.zorunlu && <span className="ml-1 font-normal text-wait">(opsiyonel)</span>}
              </p>
              {f.length > 0 && (
                <div className="mb-1.5">
                  <FotoSerit fotolar={f} />
                </div>
              )}
              <FotoEkle
                jobId={id}
                step={y.step as PhotoStep}
                etiket={f.length > 0 ? 'Bir tane daha' : `${y.ad} fotoğrafla`}
                gorunum="kompakt"
                onBitti={yukle}
              />
            </div>
          );
        })}
      </div>

      {/* Nihai ölçü — toplam SEÇİMDEN gelir, kayıtlı değerden değil. */}
      <div className="rounded-(--radius-mod) border border-line bg-surface-2 p-2.5">
        <p className="mb-1.5 text-sm font-medium text-ink-2">Nihai boyut</p>
        <OlcuListesi
          olculer={d.olculer}
          m3={nihai.m3}
          m2={nihai.m2}
          kucuk
          manuel={manuel !== null}
        />
        {d.olculer.length === 0 && !manuel && (
          <div className="mt-2">
            <p className="mb-1.5 text-xs text-warn">
              <strong>Henüz ölçü girilmemiş.</strong> Ölçü ekleyin ya da metrajı aşağıdan elle
              girin.
            </p>
            {onOlcuEkle && (
              <button
                onClick={onOlcuEkle}
                className="rounded-(--radius-mod) border border-navy px-3 py-1.5 text-xs font-medium text-navy"
              >
                ⋮ Keşif ölçüsü ekle
              </button>
            )}
          </div>
        )}
      </div>

      {/* Nihai metraj seçicisi — onay no'nun ÜSTÜNDE, masaüstüyle ORTAK bileşen. */}
      <NihaiMetrajSecici olculer={d.olculer} manuel={manuel} onDegis={setManuel} kucuk />

      {/* İskele onay no */}
      <div>
        <label className="mb-1 block text-sm font-medium text-ink-2" htmlFor="sahaOnayNo">
          İskele Onay No <span className="font-normal text-crit">(zorunlu)</span>
        </label>
        <input
          id="sahaOnayNo"
          className="num w-40 rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm outline-none focus:border-navy"
          placeholder="—"
          value={onayNo}
          onChange={(e) => setOnayNo(e.target.value)}
        />
      </div>

      <Buton disabled={mesgul || teslimEngelleri.length > 0} onClick={onTeslim}>
        <OnayIkon /> Onayla &amp; Teslim
      </Buton>
      {teslimEngelleri.length > 0 && (
        <ul className="space-y-0.5 text-xs text-warn">
          {teslimEngelleri.map((e) => (
            <li key={e}>• {e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ================================================================== */
/* SÖKÜM AKIŞI (prototip mSteps, j.durum==='sokum' dalı)              */
/* basla → devam → temiz → iade                                        */
/* ================================================================== */

interface FazBilgi {
  tip: string;
  durum: string | null;
  revizeTur: string | null;
  not: string | null;
  ekip: string[];
  basladiAt: string | null;
  bittiAt: string | null;
}

function Kabuk({
  d,
  hata,
  onGeri,
  geriEtiket,
  children,
}: {
  d: Detay;
  hata: string | null;
  onGeri: () => void;
  geriEtiket: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-lg p-4">
      <GeriDugme etiket={geriEtiket} onClick={onGeri} />
      <div className="mb-4">
        <p className="num text-sm text-wait">{d.isEmriNo ?? '—'}</p>
        <h1 className="text-base font-semibold text-navy">{d.tanim}</h1>
        <p className="text-xs text-wait">{d.unite}</p>
        <SapSatiri d={d} />
      </div>
      {hata && (
        <p className="mb-3 rounded-(--radius-mod) bg-crit/10 p-3 text-sm text-crit">{hata}</p>
      )}
      <div className="space-y-2">
        {children}
      </div>
    </div>
  );
}

function SokumAdimlari({
  d,
  faz,
  id,
  cagir,
  yukle,
  mesgul,
  hata,
  onGeri,
  geriEtiket,
  cevrimici,
}: {
  d: Detay;
  faz: FazBilgi;
  id: string;
  cagir: (yol: string, govde?: Record<string, unknown>) => Promise<void>;
  yukle: () => Promise<void>;
  mesgul: boolean;
  hata: string | null;
  onGeri: () => void;
  geriEtiket: string;
  cevrimici: boolean;
}) {
  const [acilan, setAcilan] = useState<string | null>(null);
  /* atandi → emir henüz alınmadı. Eski söküm kayıtları `basla` ve sonrasında:
     bir adım ilerlemiş görünürler ve doğrusu budur, o ekipler emri almıştı. */
  const sira =
    { atandi: 0, basla: 1, devam: 2, temiz: 3, iade: 4 }[faz.durum ?? 'atandi'] ?? 0;
  const bosAlanVar = d.fotolar.some((f) => f.step === 'bos_alan');
  const sokumIsEmriFoto = d.fotolar.filter((f) => f.step === 'sokum_is_emri');
  /**
   * "Emri aldım" adımının kapısı: ekip iki yoldan BİRİNİ seçmiş olmalı.
   * ⚠️ Sessizce geçilemez — söküm iş emri kâğıdı ya var ya yok; hangisi
   *    olduğunu kayda geçmeden ilerlemek, sonradan "kâğıt neredeydi"
   *    sorusunu cevapsız bırakır.
   */
  const [emirYolu, setEmirYolu] = useState<'mevcut' | 'yeni' | null>(null);
  /**
   * ⚠️ İŞ EMRİ FOTOSU HİÇ YOKSA "var olanı kullan" yolu KAPALI (03.08.2026).
   *    Ortada kullanılacak kâğıt yok; daha kötüsü ekip sökümü baştan sona
   *    yapıp kapanışta K3'e çarpıyordu. Sunucu da reddediyor
   *    (`sokumEmriAlinabilirMi`) — buradaki gizleme yalnız yolu gösteriyor.
   */
  const isEmriEksik = !d.isEmriFotoVar;
  const emirHazir =
    !isEmriEksik && (emirYolu === 'mevcut' || sokumIsEmriFoto.length > 0);

  const dm: Record<string, Durum> = {
    aldim: sira >= 1 ? 'done' : 'current',
    basla: sira >= 2 ? 'done' : sira === 1 ? 'current' : 'locked',
    tamam: sira >= 3 ? 'done' : sira === 2 ? 'current' : 'locked',
    temiz: sira >= 4 ? 'done' : sira === 3 ? 'current' : 'locked',
    kapat: sira === 4 ? 'current' : 'locked',
  };
  const sirada = ['aldim', 'basla', 'tamam', 'temiz', 'kapat'].find((k) => dm[k] === 'current') ?? null;
  const acik = acilan === '__kapali__' ? null : (acilan ?? sirada);
  const ac = (k: string) => setAcilan(acik === k ? '__kapali__' : k);
  const kuyruk = cevrimici ? '' : ' (kuyruğa)';

  return (
    <Kabuk d={d} hata={hata} onGeri={onGeri} geriEtiket={geriEtiket}>
      {/*
        SÖKÜM EMRİNİ ALDIM — 03.08.2026'da eklendi.
        Faz doğrudan "başladım" ile açılıyordu; ekip kendi onayını veremiyor ve
        söküm iş emri kâğıdının hangi aşamada isteneceğinin yeri bulunmuyordu.
        §6.6: "Kâğıt fotosu — söküm iş emri kâğıdı, şimdi veya sonra".
      */}
      <Adim
        durum={dm.aldim!}
        baslik="Söküm emrini aldım"
        alt={
          sira >= 1
            ? sokumIsEmriFoto.length > 0
              ? 'Söküm iş emri fotoğraflı'
              : 'Önceki iş emriyle devam edildi'
            : 'İş emri kâğıdını seç, sonra onayla'
        }
        acik={acik === 'aldim'}
        onAc={() => ac('aldim')}
      >
        {sira >= 1 ? (
          <>
            <Bilgi>Emir alındı — sıradaki adım “Söküme başladım”.</Bilgi>
            {sokumIsEmriFoto.length > 0 && (
              <div className="mt-2 space-y-2">
                <FotoSerit fotolar={sokumIsEmriFoto} />
                {/* Söküm emri de çok sayfalı gelebilir — emir alındıktan sonra da eklenebilsin. */}
                <FotoEkle
                  jobId={id}
                  step="sokum_is_emri"
                  etiket="Sayfa ekle"
                  gorunum="kompakt"
                  onBitti={yukle}
                />
              </div>
            )}
          </>
        ) : (
          <>
            {isEmriEksik ? (
              <>
                <div className="rounded-(--radius-kart) border border-crit-line bg-crit-bg p-3">
                  <p className="text-sm font-medium text-crit">
                    Bu işin iş emri kâğıdı fotoğrafı hiç yüklenmemiş.
                  </p>
                  <p className="mt-1 text-xs text-ink-2">
                    Söküm bu fotoğraf olmadan <strong>kapanamaz</strong> (K3). “Var olan iş
                    emriyle devam et” seçeneği bu yüzden yok — ortada kullanılacak kâğıt yok.
                    Şimdi yükle, sonra sökümü baştan sona yapıp kapanışta takılma.
                  </p>
                </div>
                <div className="mt-3">
                  <FotoEkle
                    jobId={id}
                    step="is_emri"
                    etiket="İş emri kâğıdını çek"
                    onBitti={yukle}
                  />
                </div>
              </>
            ) : (
              <>
                <Bilgi>
                  Söküm için ayrı bir iş emri kâğıdı geldiyse fotoğrafını yükle.
                  Gelmediyse kurulumun iş emriyle devam edilir.
                </Bilgi>

                <div className="mt-3 grid gap-2">
                  <button
                    type="button"
                    onClick={() => setEmirYolu('mevcut')}
                    className={`rounded-(--radius-mod) border p-3 text-left text-sm ${
                      emirYolu === 'mevcut'
                        ? 'border-ok bg-ok-bg text-ok'
                        : 'border-line-2 text-ink-2'
                    }`}
                  >
                    <span className="block font-medium">Var olan iş emriyle devam et</span>
                    <span className="block text-xs text-wait">
                      Söküm için ayrı kâğıt gelmedi
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setEmirYolu('yeni')}
                    className={`rounded-(--radius-mod) border p-3 text-left text-sm ${
                      emirYolu === 'yeni'
                        ? 'border-navy bg-navy-bg text-navy'
                        : 'border-line-2 text-ink-2'
                    }`}
                  >
                    <span className="block font-medium">Yeni iş emri fotoğrafı yükle</span>
                    <span className="block text-xs text-wait">Söküm iş emri kâğıdı elimde</span>
                  </button>
                </div>
              </>
            )}

            {!isEmriEksik && emirYolu === 'yeni' && (
              <div className="mt-3">
                <FotoEkle
                  jobId={id}
                  step="sokum_is_emri"
                  etiket="Söküm iş emri kâğıdını çek"
                  onBitti={yukle}
                />
                {sokumIsEmriFoto.length > 0 && (
                  <div className="mt-2">
                    <FotoSerit fotolar={sokumIsEmriFoto} />
                  </div>
                )}
              </div>
            )}

            <div className="mt-3">
              <Buton
                disabled={mesgul || !emirHazir}
                onClick={() => void cagir('sokum-ilerlet')}
              >
                ✓ Söküm emrini aldım{kuyruk}
              </Buton>
              {!emirHazir && (
                <p className="mt-1.5 text-xs text-warn">
                  {isEmriEksik
                    ? 'Önce iş emri kâğıdının fotoğrafını yükleyin.'
                    : emirYolu === 'yeni'
                      ? 'Önce söküm iş emri fotoğrafını yükleyin.'
                      : 'Yukarıdan bir seçenek seçin.'}
                </p>
              )}
            </div>
          </>
        )}
      </Adim>

      <Adim
        durum={dm.basla!}
        baslik="Söküme başladım"
        alt={sira >= 2 ? 'Başlandı — zaman damgası' : 'Söküme başlarken bas'}
        acik={acik === 'basla'}
        onAc={() => ac('basla')}
      >
        {sira >= 2 ? (
          <Bilgi>Söküm sürüyor — bitince aşağıdaki adımdan işaretleyin.</Bilgi>
        ) : (
          <Buton disabled={mesgul} onClick={() => void cagir('sokum-ilerlet')}>
            ✓ Söküme Başladım{kuyruk}
          </Buton>
        )}
      </Adim>

      <Adim
        durum={dm.tamam!}
        baslik="Söküm tamamlandı"
        alt={sira >= 3 ? 'Söküldü' : 'Söküm bitince bas (fiili damga)'}
        acik={acik === 'tamam'}
        onAc={() => ac('tamam')}
      >
        <Buton disabled={mesgul} onClick={() => void cagir('sokum-ilerlet')}>
          ✓ Söküm Tamamlandı{kuyruk}
        </Buton>
        {/* Ara fotoğraf — zorunlu DEĞİL. Sahada "şunu da kaydedeyim" denilen
            durumlar için tek yol bu; K8 gereği serbest galeri yok, adıma bağlı. */}
        <div className="mt-2">
          <FotoEkle jobId={id} step="sokum" etiket="Söküm fotoğrafı (isteğe bağlı)" onBitti={yukle} />
          <div className="mt-2">
            <FotoSerit fotolar={d.fotolar.filter((f) => f.step === 'sokum')} />
          </div>
        </div>
      </Adim>

      {/* SERT GEÇİT — boş alan fotosu ZORUNLU (kanıt) */}
      <Adim
        durum={dm.temiz!}
        gate
        baslik="Alan temizliği — boş alan fotosu"
        alt={sira >= 4 ? 'Boş alan fotosu yüklendi' : 'Söküm sonrası boş alan fotosu zorunlu'}
        acik={acik === 'temiz'}
        onAc={() => ac('temiz')}
      >
        <Bilgi>
          Söküm sonrası <strong>boş alanın</strong> fotoğrafı zorunlu — kanıt.
        </Bilgi>
        {bosAlanVar ? (
          <>
            <p className="mb-2 text-sm text-ok">✓ Boş alan fotosu eklendi</p>
            <div className="mb-3">
              <FotoSerit fotolar={d.fotolar.filter((f) => f.step === 'bos_alan')} />
            </div>
            <Buton disabled={mesgul} onClick={() => void cagir('sokum-ilerlet')}>
              ✓ Temizlik tamam{kuyruk}
            </Buton>
          </>
        ) : (
          <FotoEkle jobId={id} step="bos_alan" etiket="Boş alan fotosunu çek" onBitti={yukle} />
        )}
      </Adim>

      {/* K12 — hurda YOK, yalnız iki seçenek */}
      <Adim
        durum={dm.kapat!}
        baslik="Malzeme → kapat"
        alt="Depoya iade VEYA bölgede bırak"
        acik={acik === 'kapat'}
        onAc={() => ac('kapat')}
      >
        <div className="space-y-2">
          <Buton
            disabled={mesgul}
            onClick={() => void cagir('sokum-kapat', { malzemeAkibeti: 'depoya_iade' })}
          >
            ✓ Depoya iade et{kuyruk}
          </Buton>
          <button
            disabled={mesgul}
            onClick={() => void cagir('sokum-kapat', { malzemeAkibeti: 'bolgede_birakildi' })}
            className="w-full rounded-(--radius-mod) bg-stok px-4 py-3.5 text-base font-medium text-white disabled:opacity-40"
          >
            Bölgede bırakıldı{kuyruk}
          </button>
        </div>
      </Adim>
    </Kabuk>
  );
}

/* ================================================================== */
/* REVİZE AKIŞI — aldim → basladi → bitti → şef onayı                  */
/* ================================================================== */

/**
 * ⚠️ ŞEF REVİZEYİ MOBİLDE DE ONAYLAR (müşteri bildirimi 06.10.2026).
 *
 * Kurulumda aynı boşluk 04.08'de kapatılmıştı (`TeslimPaneli`), revizede
 * kalmıştı: şef işi telefonda açınca ekibin adımlarını ve "sende işlem yok"
 * yazısını görüyordu. Onay ancak masaüstünden verilebiliyordu; şef her onayı
 * sabah bilgisayar başında verdi, biri 6 gün bekledi.
 *
 * Panel masaüstündeki `IsDetay/RevizeOnay` ile AYNI çağrıyı yapar. K1 (revizeyi
 * yapan onaylayamaz) sunucuda zorlanır; ekipteki şef düğmeyi görür, sunucu
 * reddeder ve gerekçe üstteki hata şeridinde çıkar. Teslim paneliyle aynı.
 */
function RevizeAdimlari({
  d,
  faz,
  id,
  sefYetkisi,
  cagir,
  yukle,
  mesgul,
  hata,
  onGeri,
  geriEtiket,
  cevrimici,
}: {
  d: Detay;
  faz: FazBilgi;
  id: string;
  sefYetkisi: boolean;
  cagir: (yol: string, govde?: Record<string, unknown>) => Promise<void>;
  yukle: () => Promise<void>;
  mesgul: boolean;
  hata: string | null;
  onGeri: () => void;
  geriEtiket: string;
  cevrimici: boolean;
}) {
  const [acilan, setAcilan] = useState<string | null>(null);
  /* atandi → henüz alınmadı. Eski kayıtlar `aldim`de: bir adım ilerlemiş
     görünürler ve doğrusu budur — o ekipler işi zaten almıştı. */
  const sira = { atandi: 0, aldim: 1, basladi: 2, bitti: 3, onay: 4 }[faz.durum ?? 'atandi'] ?? 0;
  const revizeFotolari = d.fotolar.filter((f) => f.step === 'revize');

  /* Her adımın DÜĞMESİ kendi başlığını yapar — başlık ile eylem kaymasın. */
  const dm: Record<string, Durum> = {
    aldim: sira >= 1 ? 'done' : 'current',
    basla: sira >= 2 ? 'done' : sira === 1 ? 'current' : 'locked',
    bitti: sira >= 3 ? 'done' : sira === 2 ? 'current' : 'locked',
    onay: sira >= 4 ? 'done' : sira === 3 ? 'current' : 'locked',
  };
  const sirada = ['aldim', 'basla', 'bitti', 'onay'].find((k) => dm[k] === 'current') ?? null;
  const acik = acilan === '__kapali__' ? null : (acilan ?? sirada);
  const ac = (k: string) => setAcilan(acik === k ? '__kapali__' : k);
  const kuyruk = cevrimici ? '' : ' (kuyruğa)';

  return (
    <Kabuk d={d} hata={hata} onGeri={onGeri} geriEtiket={geriEtiket}>
      <Adim
        durum={dm.aldim!}
        baslik="Revizeyi aldım"
        alt={`${faz.revizeTur ?? 'Revizyon'} · ekip belli${faz.not ? ` · ${faz.not}` : ''}`}
        acik={acik === 'aldim'}
        onAc={() => ac('aldim')}
      >
        {sira >= 1 ? (
          <Bilgi>Revize alındı — sıradaki adım “Revizeye başladık”.</Bilgi>
        ) : (
          <Buton disabled={mesgul} onClick={() => void cagir('revize-ilerlet')}>
            ✓ Revizeyi aldım{kuyruk}
          </Buton>
        )}
      </Adim>

      <Adim
        durum={dm.basla!}
        baslik="Revizeye başladık"
        alt={sira >= 2 ? 'Çalışma sürüyor' : 'Alındıktan sonra'}
        acik={acik === 'basla'}
        onAc={() => ac('basla')}
      >
        {sira >= 2 ? (
          <Bilgi>Çalışma sürüyor — bitince aşağıdaki adımdan kapatın.</Bilgi>
        ) : (
          <Buton disabled={mesgul} onClick={() => void cagir('revize-ilerlet')}>
            ✓ Revizeye başladık{kuyruk}
          </Buton>
        )}
      </Adim>

      <Adim
        durum={dm.bitti!}
        gate
        baslik="Revizeyi bitirdik"
        alt={sira >= 3 ? 'Şef kontrol & onayına gitti' : 'Çalışma sonrası'}
        acik={acik === 'bitti'}
        onAc={() => ac('bitti')}
      >
        {sira >= 3 ? (
          <>
            <Bilgi>Bitti, şef kontrol &amp; onayına gitti.</Bilgi>
            {revizeFotolari.length > 0 && <FotoSerit fotolar={revizeFotolari} />}
          </>
        ) : (
          <>
            {/*
              FOTOĞRAF ZORUNLU (31.07.2026). Şefin onaylayacağı şey revize SONRASI
              durumdur; fotoğrafsız onay "bana öyle dediler"e dayanır. Aynı kural
              sunucuda da var — düğmeyi söndürmek yetmez, istek de reddedilir.
            */}
            <div className="mb-2">
              <FotoEkle jobId={id} step="revize" etiket="Revize fotoğrafı çek" onBitti={yukle} />
            </div>
            {revizeFotolari.length > 0 && (
              <div className="mb-2">
                <FotoSerit fotolar={revizeFotolari} />
              </div>
            )}
            <Buton
              disabled={mesgul || revizeFotolari.length === 0}
              onClick={() => void cagir('revize-ilerlet')}
            >
              ✓ Revizeyi bitirdik{kuyruk}
            </Buton>
            {revizeFotolari.length === 0 && (
              <p className="mt-1.5 text-xs text-warn">
                Bitirmek için önce revize fotoğrafı yükleyin.
              </p>
            )}
          </>
        )}
      </Adim>

      {/* Onay şefte (K1). Saha rolü için bilgilendirme, şef için panel. */}
      <Adim
        durum={dm.onay!}
        gate
        baslik={sefYetkisi ? 'Kontrol & onay' : 'Kontrol & onay (şef)'}
        alt={
          sira < 3
            ? 'Bitişten sonra'
            : sefYetkisi
              ? 'Fotoğrafa bak, boyutu kontrol et, onayla'
              : 'Şef onayında (sende işlem yok)'
        }
        acik={acik === 'onay'}
        onAc={() => ac('onay')}
      >
        {sira === 3 && sefYetkisi ? (
          <RevizeOnayPaneli
            d={d}
            fotolar={revizeFotolari}
            mesgul={mesgul}
            onOnay={(yeniMetraj) => void cagir('revize-onay', { yeniMetraj })}
          />
        ) : (
          <Bilgi>
            Şef onayında, sende işlem yok. Onaylanınca iskele tekrar kurulu olur ve iş listenden
            düşer.
          </Bilgi>
        )}
      </Adim>
    </Kabuk>
  );
}

/**
 * REVİZE ONAY PANELİ (mobil). Masaüstündeki `IsDetay/RevizeOnay` ile aynı
 * soru ve aynı çağrı: boyut değiştiyse yeni metraj m³ olarak girilir.
 * Değişecekse ikisi birlikte değişmeli.
 */
function RevizeOnayPaneli({
  d,
  fotolar,
  mesgul,
  onOnay,
}: {
  d: Detay;
  fotolar: Detay['fotolar'];
  mesgul: boolean;
  onOnay: (yeniMetraj: number | null) => void;
}) {
  const [degisti, setDegisti] = useState(false);
  const [yeni, setYeni] = useState('');
  const sayi = Number(yeni.replace(',', '.'));
  const yeniGecerli = yeni.trim() !== '' && Number.isFinite(sayi) && sayi > 0;

  return (
    <div className="space-y-3">
      <Bilgi>
        <span className="text-xs">Revizeyi yapan kişi kendi revizesini onaylayamaz (K1).</span>
      </Bilgi>

      {/* Onay revize SONRASI hâle bakarak verilir; fotoğraf bitişte zorunlu. */}
      {fotolar.length > 0 && <FotoSerit fotolar={fotolar} />}

      <div className="rounded-(--radius-mod) border border-line bg-surface-2 p-2.5">
        <label className="flex items-center gap-2 text-sm font-medium text-ink-2">
          <input type="checkbox" checked={degisti} onChange={(e) => setDegisti(e.target.checked)} />
          Boyut değişti
        </label>
        <p className="mt-1 text-xs text-wait">Şu anki metraj: {metrajGoster(d)}</p>
        {degisti && (
          <input
            className="num mt-2 w-40 rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm outline-none focus:border-navy"
            inputMode="decimal"
            placeholder="yeni m³"
            value={yeni}
            onChange={(e) => setYeni(e.target.value)}
          />
        )}
      </div>

      <Buton
        disabled={mesgul || (degisti && !yeniGecerli)}
        onClick={() => onOnay(degisti && yeniGecerli ? sayi : null)}
      >
        <OnayIkon /> Revizeyi onayla
      </Buton>
      {degisti && !yeniGecerli && (
        <p className="text-xs text-warn">Boyut değiştiyse yeni metrajı girin.</p>
      )}
    </div>
  );
}
